import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Header from './shared/Header';
import Footer from './shared/Footer';
import usePageMetadata from '../utils/usePageMetadata';
import {
  Bot,
  BookOpen,
  GitBranch,
  Database,
  ArrowRight,
  CheckCircle,
} from 'lucide-react';

const services = [
  {
    id: 'claude',
    icon: <Bot size={28} />,
    headline: 'Claude Implementation & Integration',
    description:
      'We stand up Claude in production, integrated into your existing products, internal tools, or customer-facing applications. From first API call to production deployment, we handle the engineering and the judgment calls.',
    deliverables: [
      'Production Claude deployments integrated with your stack',
      'Prompt engineering, evals, and guardrails',
      'Integration with existing products, internal tools, and customer-facing apps',
    ],
  },
  {
    id: 'rag',
    icon: <BookOpen size={28} />,
    headline: 'RAG & Knowledge Systems',
    details: '/services/rag-implementation',
    description:
      'We build retrieval-augmented generation (RAG) systems over your proprietary documents and data. The goal is useful, source-grounded answers, measured against real questions. Retrieval does not eliminate model errors: evaluation, access controls, and a clear fallback when evidence is missing belong in the implementation scope.',
    deliverables: [
      'Custom retrieval over proprietary data sources',
      'Vector databases, embedding pipelines, hybrid search',
      'Document ingestion, chunking, and indexing strategies',
    ],
  },
  {
    id: 'agents',
    icon: <GitBranch size={28} />,
    headline: 'AI Agent Implementation & Workflow Automation',
    description:
      'We build workflows across your existing systems, using explicit rules for predictable steps and AI where interpretation is useful. An agent is one option, not a requirement. Scope the integrations, approvals, exception handling, and operating responsibilities before increasing autonomy.',
    deliverables: [
      'Workflow mapping, integration boundaries, and decision rules',
      'Bounded AI steps or agent orchestration where the task warrants it',
      'Human approvals, exception paths, and handover requirements',
    ],
  },
  {
    id: 'data',
    icon: <Database size={28} />,
    headline: 'Data Infrastructure & Analytics',
    description:
      'Modern data stacks that actually move data where it needs to go. We architect, build, and operate pipelines for teams that have outgrown their current setup. DataBackfill Sync, our own GA4 to BigQuery product, is a live example of this work.',
    deliverables: [
      'GA4, BigQuery, and data warehouse architecture',
      'ETL and reverse-ETL pipelines',
      'DataBackfill Sync as a productized reference implementation',
    ],
  },
];

const ServicesPage = () => {
  const { hash } = useLocation();
  usePageMetadata({
    title: 'AI Implementation & Forward Deployed Engineering | DataBackfill',
    description: 'Scope AI agent implementation, RAG knowledge systems, Claude integration, and workflow automation with DataBackfill. Start with your workflow, data, and delivery constraints.',
    path: '/services',
  });

  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.slice(1));
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
      }
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [hash]);

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Page header */}
      <section className="pt-24 md:pt-32 pb-12 md:pb-16 bg-gradient-to-br from-blue-50 via-white to-white">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
          <span className="inline-block px-3 py-1 bg-blue-50 text-blue-600 rounded-full text-sm font-medium mb-6">
            What we build
          </span>
          <h1 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6 max-w-2xl leading-tight">
            AI implementation & forward deployed engineering
          </h1>
          <p className="text-lg md:text-xl text-gray-600 max-w-2xl leading-relaxed">
            DataBackfill helps teams build AI agents, RAG knowledge systems, and workflow
            automation. Bring a workflow that needs improving or a prototype that needs
            production engineering. Start with the problem, the systems it touches, and
            what a successful result would look like.
          </p>
        </div>
      </section>

      <section className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 pt-12" aria-labelledby="project-fit">
        <h2 id="project-fit" className="text-2xl font-bold text-gray-900 mb-4">Which implementation fits your problem?</h2>
        <p className="text-gray-600 max-w-3xl mb-6 leading-relaxed">
          Forward deployed engineering connects hands-on software delivery with your
          operating context. The starting point is a bounded workflow and a team that
          can explain the data, review outputs, and define acceptable failure behavior.
          Not every process needs an autonomous agent.
        </p>
        <ul className="space-y-3 text-gray-700">
          <li><Link className="text-blue-700 underline" to="#rag">RAG and knowledge systems</Link>: people need answers grounded in internal documents, with the right access permissions.</li>
          <li><Link className="text-blue-700 underline" to="#agents">Agents and workflow automation</Link>: a task spans tools, decisions, and handoffs. Define which actions need human approval. <Link className="text-blue-700 underline" to="/services/ai-agent-implementation">Explore AI agent implementation scope and costs</Link>.</li>
          <li><Link className="text-blue-700 underline" to="#claude">Claude integration</Link>: an existing product or internal tool needs a model-backed capability with testing and error handling.</li>
          <li><Link className="text-blue-700 underline" to="#data">Data infrastructure</Link>: missing, stale, or disconnected data is the underlying problem.</li>
        </ul>
      </section>

      {/* Service sections */}
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20 space-y-20 md:space-y-28">
        {services.map((service, index) => (
          <section key={service.id} id={service.id} className="scroll-mt-24">
            <div className={`grid grid-cols-1 lg:grid-cols-2 gap-10 items-start ${
              index % 2 === 1 ? 'lg:grid-flow-dense' : ''
            }`}>
              {/* Text side */}
              <div className={index % 2 === 1 ? 'lg:col-start-2' : ''}>
                <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center mb-6 text-blue-600">
                  {service.icon}
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                  {service.headline}
                </h2>
                <p className="text-gray-600 leading-relaxed mb-8 text-base md:text-lg">
                  {service.description}
                </p>
                {service.id === 'rag' && <p className="mb-6">
                  <Link className="text-blue-700 underline" to="/blog/rag-that-does-not-hallucinate">
                    Read the RAG implementation buyer’s guide
                  </Link>
                </p>}
                <Link
                  to="/contact"
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  className="inline-flex items-center px-5 py-2.5 bg-blue-600 hover:bg-blue-700
                             text-white rounded-lg font-medium transition-colors text-sm"
                >
                  Talk to us
                  <ArrowRight className="ml-2" size={16} />
                </Link>
              </div>

              {/* Deliverables card */}
              <div className={`bg-gray-50 rounded-2xl p-8 border border-gray-100 ${
                index % 2 === 1 ? 'lg:col-start-1 lg:row-start-1' : ''
              }`}>
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-6">
                  What you get
                </h3>
                <ul className="space-y-4">
                  {service.deliverables.map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckCircle size={18} className="text-blue-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700 text-sm leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
                {service.details && <p className="mt-6"><Link className="text-blue-700 underline" to={service.details}>Explore RAG implementation scope</Link></p>}
              </div>
            </div>

            {/* Divider between sections */}
            {index < services.length - 1 && (
              <div className="border-b border-gray-100 mt-20 md:mt-28" />
            )}
          </section>
        ))}
      </div>

      <section className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 pb-16 scroll-mt-24" aria-labelledby="workflow-scope">
        <h2 id="workflow-scope" className="scroll-mt-24 text-2xl md:text-3xl font-bold text-gray-900 mb-6">Scope workflow automation before choosing an agent</h2>
        <p className="text-gray-600 max-w-3xl leading-relaxed mb-5">
          Start with one recurring process and its owner. If the sequence and decision
          rules are known, a fixed workflow may be enough. A model might classify an
          incoming request or draft a response without controlling the entire process.
          Anthropic’s distinction between predefined workflows and dynamically directed
          agents is a useful technical reference, not a reason to automate every step.
        </p>
        <p className="mb-6"><a className="text-blue-700 underline" href="https://www.anthropic.com/engineering/building-effective-agents">Technical reference: workflows versus agents</a></p>
        <h3 className="text-lg font-semibold text-gray-900 mb-3">A practical first-workflow brief</h3>
        <ul className="list-disc pl-6 space-y-3 text-gray-700 leading-relaxed max-w-3xl">
          <li>Trigger and volume: what starts the work, how often it happens, and who owns the queue.</li>
          <li>Systems and actions: which records must be read or changed, which integrations are available, and who can authorize access.</li>
          <li>Approvals and exceptions: which actions require review, who receives incomplete or unusual cases, and how work resumes after a failure.</li>
          <li>Acceptance checks: include duplicate requests, missing fields, unavailable integrations, rejected approvals, and the expected recovery behavior.</li>
          <li>Baseline and ownership: record current handling time and rework, then agree how outcomes and ongoing operating costs will be measured.</li>
        </ul>
        <p className="text-gray-600 max-w-3xl leading-relaxed mt-6">
          Illustrative scope, not a customer result: collect an onboarding request,
          check required fields, prepare an internal task, and route access changes to
          an authorized reviewer. A missing approval should leave the action pending,
          not silently grant access. Agree the treatment of timeouts and repeated
          requests before connecting a live system.
        </p>
        <p className="text-gray-600 max-w-3xl leading-relaxed mt-5">
          If the process has no owner or its rules keep changing, document those gaps
          first. Automation does not settle unclear policy. An estimate should separate
          implementation, third-party usage, and ongoing maintenance; no savings or
          delivery date can be inferred from the workflow label alone.
        </p>
        <p className="mt-6"><Link className="text-blue-700 underline" to="/contact">Discuss a workflow and its approval boundaries</Link></p>
      </section>

      <section className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 pb-16" aria-labelledby="scope">
        <h2 id="scope" className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">What to define before an implementation</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-gray-600 leading-relaxed">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Workflow and success criteria</h3>
            <p>Describe the current process, who uses it, and the cost of a wrong result. Pick a bounded first use case. Define examples of acceptable outputs, escalation cases, and a baseline to compare against.</p>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Data and system access</h3>
            <p>Identify source systems, document formats, APIs, permission boundaries, and deployment constraints. Start with a description or sanitized example, not credentials or confidential records.</p>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Evaluation and operational limits</h3>
            <p>Agree how quality, latency, and operating cost will be measured. Include failure cases, human review, logging needs, and limits on the actions an agent can take.</p>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Ownership and handover</h3>
            <p>Make repository access, deployment ownership, documentation, monitoring, and support responsibilities explicit in the scope. Clarify who maintains the system when data, models, or business rules change.</p>
          </div>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mt-12 mb-4">What affects implementation cost and timing?</h2>
        <p className="text-gray-600 max-w-3xl leading-relaxed">
          The number of integrations, data preparation, permission requirements,
          evaluation coverage, and deployment constraints shape the work. A document
          assistant and an agent allowed to change records have different testing and
          review needs. Share those constraints when requesting an estimate; a model
          choice alone is not enough to scope a project.
        </p>
        <p className="text-gray-600 mt-6">
          Explore our <Link to="/blog" className="text-blue-700 underline">engineering field notes</Link> or
          review <Link to="/work" className="text-blue-700 underline">our work</Link> before discussing your project.
        </p>
      </section>

      {/* Bottom CTA */}
      <section className="py-12 md:py-20 bg-gray-50">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-lg text-gray-700 mb-6">
            Tell us the workflow, systems involved, and what you need to improve.
          </p>
          <Link
            to="/contact"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="inline-flex items-center px-6 py-3 bg-blue-600 hover:bg-blue-700
                       text-white rounded-xl font-medium transition-all shadow-md hover:shadow-lg"
          >
            Discuss your implementation
            <ArrowRight className="ml-2" size={18} />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServicesPage;
